import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Container,
    Alert,
    CircularProgress,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    InputAdornment,
    Divider,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../components/AuthContext';

const ChavesApiPage: React.FC = () => {
    const { loading: authLoading } = useAuth();
    const [geminiApiKey, setGeminiApiKey] = useState('');
    const [resendApiKey, setResendApiKey] = useState('');
    const [awsAccessKey, setAwsAccessKey] = useState('');
    const [awsSecretKey, setAwsSecretKey] = useState('');
    const [awsRegion, setAwsRegion] = useState('');
    const [smsProvider, setSmsProvider] = useState('AWS');
    const [comteleApiKey, setComteleApiKey] = useState('');

    const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
    const [configLoading, setConfigLoading] = useState(true);
    const [configSaving, setConfigSaving] = useState(false);
    const [configMessage, setConfigMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const toggleShowKey = (field: string) => {
        setShowKeys((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    useEffect(() => {
        const fetchConfig = async () => {
            setConfigLoading(true);
            try {
                const { data, error } = await supabase
                    .from('configuracoes_sistema')
                    .select('chave, valor')
                    .in('chave', [
                        'GEMINI_API_KEY',
                        'RESEND_API_KEY',
                        'AWS_ACCESS_KEY_ID',
                        'AWS_SECRET_ACCESS_KEY',
                        'AWS_REGION',
                        'SMS_GATEWAY_PROVIDER',
                        'COMTELE_API_KEY',
                    ]);

                if (error && error.code !== 'PGRST116') throw error;
                if (data) {
                    const byKey: Record<string, string> = {};
                    data.forEach((d: any) => {
                        byKey[d.chave] = d.valor || '';
                    });

                    if (byKey.GEMINI_API_KEY !== undefined) setGeminiApiKey(byKey.GEMINI_API_KEY);
                    if (byKey.RESEND_API_KEY !== undefined) setResendApiKey(byKey.RESEND_API_KEY);
                    if (byKey.AWS_ACCESS_KEY_ID !== undefined) setAwsAccessKey(byKey.AWS_ACCESS_KEY_ID);
                    if (byKey.AWS_SECRET_ACCESS_KEY !== undefined) setAwsSecretKey(byKey.AWS_SECRET_ACCESS_KEY);
                    if (byKey.AWS_REGION !== undefined) setAwsRegion(byKey.AWS_REGION);
                    if (byKey.SMS_GATEWAY_PROVIDER) setSmsProvider(byKey.SMS_GATEWAY_PROVIDER);
                    if (byKey.COMTELE_API_KEY !== undefined) setComteleApiKey(byKey.COMTELE_API_KEY);
                }
            } catch (err: any) {
                console.error('Erro ao buscar configurações:', err);
                setConfigMessage({ type: 'error', text: err.message || 'Erro ao carregar chaves de API.' });
            } finally {
                setConfigLoading(false);
            }
        };

        fetchConfig();
    }, []);

    const handleSaveConfig = async (e: React.FormEvent) => {
        e.preventDefault();
        setConfigSaving(true);
        setConfigMessage(null);
        try {
            const payload = [
                {
                    chave: 'GEMINI_API_KEY',
                    valor: geminiApiKey.trim(),
                    descricao: 'Chave de API do Google Gemini AI Studio',
                },
                {
                    chave: 'RESEND_API_KEY',
                    valor: resendApiKey.trim(),
                    descricao: 'Chave de API do Resend (Envio de E-mails)',
                },
                {
                    chave: 'AWS_ACCESS_KEY_ID',
                    valor: awsAccessKey.trim(),
                    descricao: 'AWS Access Key ID para SMS',
                },
                {
                    chave: 'AWS_SECRET_ACCESS_KEY',
                    valor: awsSecretKey.trim(),
                    descricao: 'AWS Secret Access Key para SMS',
                },
                {
                    chave: 'AWS_REGION',
                    valor: awsRegion.trim(),
                    descricao: 'AWS Region (ex: us-east-1)',
                },
                {
                    chave: 'SMS_GATEWAY_PROVIDER',
                    valor: smsProvider,
                    descricao: 'Provedor de SMS Ativo',
                },
                {
                    chave: 'COMTELE_API_KEY',
                    valor: comteleApiKey.trim(),
                    descricao: 'Chave de API da Comtele',
                },
            ];

            const { error } = await supabase.from('configuracoes_sistema').upsert(payload);
            if (error) throw error;

            setConfigMessage({ type: 'success', text: 'Chaves de API salvas com sucesso!' });
        } catch (err: any) {
            console.error('Erro ao salvar configuração:', err);
            setConfigMessage({ type: 'error', text: err.message || 'Falha ao salvar as Chaves de API.' });
        } finally {
            setConfigSaving(false);
        }
    };

    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const isGeminiConfigured = Boolean(geminiApiKey.trim());
    const isResendConfigured = Boolean(resendApiKey.trim());
    const isSmsConfigured =
        smsProvider === 'AWS'
            ? Boolean(awsAccessKey.trim() && awsSecretKey.trim() && awsRegion.trim())
            : Boolean(comteleApiKey.trim());

    return (
        <Container component="main" maxWidth="md">
            <Box sx={{ my: 4 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
                        Gerenciar Chaves API
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configure as credenciais de integrações externas utilizadas pelo sistema (IA, E-mail e SMS)
                    </Typography>
                </Box>

                {configMessage && (
                    <Alert
                        severity={configMessage.type}
                        sx={{ mb: 3 }}
                        onClose={() => setConfigMessage(null)}
                    >
                        {configMessage.text}
                    </Alert>
                )}

                {configLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSaveConfig} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Seção 1: Gemini AI */}
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AutoAwesomeIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        Inteligência Artificial (Google Gemini)
                                    </Typography>
                                </Box>
                                <Chip
                                    label={isGeminiConfigured ? 'Configurada' : 'Não configurada'}
                                    color={isGeminiConfigured ? 'success' : 'default'}
                                    size="small"
                                    variant={isGeminiConfigured ? 'outlined' : 'filled'}
                                    sx={{ fontWeight: 600 }}
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Habilita a leitura e extração automática de itens de notas fiscais e cupons via IA.
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                label="Chave da API (Gemini AI Studio)"
                                type={showKeys.gemini ? 'text' : 'password'}
                                value={geminiApiKey}
                                onChange={(e) => setGeminiApiKey(e.target.value)}
                                placeholder="AIzaSy..."
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => toggleShowKey('gemini')} edge="end" size="small">
                                                {showKeys.gemini ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Paper>

                        {/* Seção 2: Resend E-mails */}
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EmailOutlinedIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        Envio de E-mails (Resend)
                                    </Typography>
                                </Box>
                                <Chip
                                    label={isResendConfigured ? 'Configurada' : 'Não configurada'}
                                    color={isResendConfigured ? 'success' : 'default'}
                                    size="small"
                                    variant={isResendConfigured ? 'outlined' : 'filled'}
                                    sx={{ fontWeight: 600 }}
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Utilizada para envio de links e códigos de assinatura digital de recibos por e-mail.
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                label="Chave da API (Resend)"
                                type={showKeys.resend ? 'text' : 'password'}
                                value={resendApiKey}
                                onChange={(e) => setResendApiKey(e.target.value)}
                                placeholder="re_..."
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => toggleShowKey('resend')} edge="end" size="small">
                                                {showKeys.resend ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Paper>

                        {/* Seção 3: SMS Gateway */}
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SmsOutlinedIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        Gateway de SMS (Assinatura Digital)
                                    </Typography>
                                </Box>
                                <Chip
                                    label={isSmsConfigured ? `Configurada (${smsProvider})` : 'Não configurada'}
                                    color={isSmsConfigured ? 'success' : 'default'}
                                    size="small"
                                    variant={isSmsConfigured ? 'outlined' : 'filled'}
                                    sx={{ fontWeight: 600 }}
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Escolha o provedor de SMS utilizado para enviar códigos de verificação e links de assinatura.
                            </Typography>

                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel id="sms-provider-label">Provedor de SMS Ativo</InputLabel>
                                <Select
                                    labelId="sms-provider-label"
                                    value={smsProvider}
                                    label="Provedor de SMS Ativo"
                                    onChange={(e) => setSmsProvider(e.target.value)}
                                >
                                    <MenuItem value="AWS">Amazon AWS (SNS)</MenuItem>
                                    <MenuItem value="COMTELE">Comtele</MenuItem>
                                </Select>
                            </FormControl>

                            <Divider sx={{ mb: 2 }} />

                            {smsProvider === 'AWS' ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="AWS Access Key ID"
                                        value={awsAccessKey}
                                        onChange={(e) => setAwsAccessKey(e.target.value)}
                                        placeholder="AKIA..."
                                    />
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="AWS Secret Access Key"
                                        type={showKeys.awsSecret ? 'text' : 'password'}
                                        value={awsSecretKey}
                                        onChange={(e) => setAwsSecretKey(e.target.value)}
                                        placeholder="Segredo..."
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => toggleShowKey('awsSecret')} edge="end" size="small">
                                                        {showKeys.awsSecret ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="AWS Region"
                                        value={awsRegion}
                                        onChange={(e) => setAwsRegion(e.target.value)}
                                        placeholder="us-east-1"
                                    />
                                </Box>
                            ) : (
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Comtele API Key (auth-key)"
                                    type={showKeys.comtele ? 'text' : 'password'}
                                    value={comteleApiKey}
                                    onChange={(e) => setComteleApiKey(e.target.value)}
                                    placeholder="Sua chave de API Comtele"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => toggleShowKey('comtele')} edge="end" size="small">
                                                    {showKeys.comtele ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        </Paper>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                startIcon={configSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                disabled={configSaving}
                                sx={{ fontWeight: 600, px: 4, borderRadius: 2 }}
                            >
                                {configSaving ? 'Salvando...' : 'Salvar Configurações'}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default ChavesApiPage;
